<template>
  <div class="login-page">
    <div class="login-stack">
      <img :src="logoSrc" alt="HR" width="150" height="130" class="login-logo" />
      <Card class="login-card">
        <template #content>
          <form @submit.prevent="handleLogin" autocomplete="on" :aria-busy="isLoading">
            <div class="login-field">
              <FloatLabel variant="on">
                <InputText
                  id="username" name="username" v-model="username" type="email"
                  required class="w-full" autocomplete="username"
                  autocapitalize="none" :spellcheck="false" :readonly="isLoading"
                />
                <label for="username">Account email</label>
              </FloatLabel>
            </div>
            <div class="login-field login-password">
              <FloatLabel variant="on">
                <InputText
                  id="password" name="password" v-model="password" required
                  class="w-full" :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password" :readonly="isLoading"
                />
                <label for="password">Password</label>
              </FloatLabel>
              <button
                type="button" class="password-toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword" aria-controls="password"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" aria-hidden="true"></i>
              </button>
            </div>
            <div class="login-remember">
              <div class="login-remember-option">
                <Checkbox v-model="rememberMe" :binary="true" inputId="rememberMe" :disabled="isLoading" />
                <label for="rememberMe">Remember me</label>
              </div>
              <p class="login-hint">Save password with your browser.</p>
            </div>
            <Button label="Login" type="submit" class="submit-button" :loading="isLoading" :disabled="isLoading" severity="primary" />
          </form>
          <div class="login-theme">
            <Button
              :icon="themeIcon" class="theme-toggle-btn" severity="secondary" text
              :aria-label="themeIcon === 'pi pi-sun' ? 'Switch to light theme' : 'Switch to dark theme'"
              @click="toggleTheme"
            />
          </div>
        </template>
      </Card>
      <p class="login-version" aria-label="Application version">Version {{ appVersionLabel }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import FloatLabel from 'primevue/floatlabel';
import Checkbox from 'primevue/checkbox';

import { useToast } from 'primevue/usetoast';
import api from '@/utils/axios';
import { getDefaultAuthedRoute } from '@/utils/getDefaultAuthedRoute';
import { formatAppVersionLabel } from '@/constants/appMeta';
import { saveBrowserLogin } from '@/utils/browserLogin';

const isDev =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) ||
  (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production');

export default {
  components: { Button, InputText, Card, FloatLabel, Checkbox },
  setup() {
    const toast = useToast();
    const store = useStore();
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const showPassword = ref(false);
    const isLoading = ref(false);
    const rememberMe = ref(false);

    const LAST_EMAIL_KEY = 'hr_last_login_email';
    const REMEMBER_ME_KEY = 'hr_remember_me';
    // Passwords are never persisted. Earlier builds stored them, so purge those keys on mount.
    const PURGED_PASSWORD_KEYS = ['hr_remembered_password', 'dcr_remembered_password'];

    const toggleTheme = () => {
      const newTheme = currentTheme.value === 'dark' ? 'light' : 'dark';
      currentTheme.value = newTheme;
      document.documentElement.classList.remove('dark-mode');
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark-mode');
      }
      store.dispatch('changeTheme', newTheme);
    };

    const persistRememberPreference = () => {
      try {
        const email = username.value?.trim() || '';
        if (rememberMe.value && email) {
          localStorage.setItem(REMEMBER_ME_KEY, '1');
          localStorage.setItem(LAST_EMAIL_KEY, email);
          return;
        }
        localStorage.removeItem(REMEMBER_ME_KEY);
        localStorage.removeItem(LAST_EMAIL_KEY);
      } catch (error) { /* Storage may be unavailable in private browsing. */ }
    };

    watch(rememberMe, persistRememberPreference);

    const handleLogin = async () => {
      if (isLoading.value) return;
      const email = username.value.trim();
      const submittedPassword = password.value;
      const shouldRemember = rememberMe.value;

      try {
        isLoading.value = true;

        if (isDev) {
          console.log('Login attempt:', { email: username.value });
        }

        const response = await api.post('/users/login', {
          email,
          pass: submittedPassword
        });

        if (isDev) {
          console.log('Login response:', response?.data, response?.status);
        }

        const data = response.data;

        if (data.success) {
          await store.dispatch('users/loginUser', data);
          const meOk = await store.dispatch('users/fetchMe');
          if (!meOk) {
            toast.add({
              severity: 'error',
              summary: 'Session Error',
              detail: 'Could not load your profile. Please try again.',
              life: 5000
            });
            return;
          }

          persistRememberPreference();
          // Keep the submitted form intact until navigation for native password managers.
          // Browser approval is independent of authentication and must never block it.
          void saveBrowserLogin({ email, password: submittedPassword, remember: shouldRemember });

          toast.add({
            severity: 'success',
            summary: 'Welcome!',
            detail: 'Login successful',
            life: 3000
          });
          router.push(getDefaultAuthedRoute(store.state.users?.user));
        } else {
          if (isDev) {
            console.log('Login failed:', data?.type, data?.message);
          }

          // Backend rate limit: show server message only (protection is on backend)
          if (data.type === 'rate_limit') {
            toast.add({
              severity: 'error',
              summary: 'Rate Limited',
              detail: data.message,
              life: 5000
            });
            return;
          }

          const errorMessage = data.message || 'Login failed';
          toast.add({
            severity: 'error',
            summary: 'Login Failed',
            detail: errorMessage,
            life: 3000
          });
        }
      } catch (error) {
        if (isDev) {
          console.error('Login error:', error?.message, error?.response?.data);
        }

        const isNetworkError = error.code === 'ERR_NETWORK';
        const errorMessage = isNetworkError
          ? 'Server connection failed. Please check your network connection.'
          : (error.response?.data?.message || 'Login failed. Please try again later.');
        const summary = isNetworkError ? 'Connection Error' : 'Login Failed';

        toast.add({
          severity: 'error',
          summary,
          detail: errorMessage,
          life: 5000
        });
      } finally {
        isLoading.value = false;
      }
    };


    const logoSrc = computed(() => `${process.env.BASE_URL}icons/favicon.svg`);

    const currentTheme = ref(localStorage.getItem('isTheme') || 'dark');

    const themeIcon = computed(() => {
      return currentTheme.value === 'dark' ? 'pi pi-sun' : 'pi pi-moon';
    });

    const appVersionLabel = formatAppVersionLabel();

    const migrateLegacyLoginStorage = () => {
      const legacyMap = [
        ['dcr_last_login_email', LAST_EMAIL_KEY],
        ['dcr_remember_me', REMEMBER_ME_KEY],
      ];
      for (const [legacyKey, newKey] of legacyMap) {
        const value = localStorage.getItem(legacyKey);
        if (value !== null && localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, value);
        }
        if (value !== null) {
          localStorage.removeItem(legacyKey);
        }
      }
      PURGED_PASSWORD_KEYS.forEach((key) => localStorage.removeItem(key));
    };

    onMounted(() => {
      try {
        migrateLegacyLoginStorage();

        rememberMe.value = localStorage.getItem(REMEMBER_ME_KEY) === '1';
        if (rememberMe.value) {
          const savedEmail = localStorage.getItem(LAST_EMAIL_KEY);
          if (savedEmail) username.value = savedEmail;
        } else {
          localStorage.removeItem(LAST_EMAIL_KEY);
        }

      } catch (error) { /* Login remains usable when storage is blocked. */ }

      store.dispatch('initializeTheme').then(() => {
        const isTheme = localStorage.getItem('isTheme') || 'dark';
        currentTheme.value = isTheme;
        if (isTheme === 'dark') {
          document.documentElement.classList.add("dark-mode");
        } else {
          document.documentElement.classList.remove("dark-mode");
        }
      });
    });

    return { username, password, showPassword, rememberMe, isLoading, handleLogin, toggleTheme, logoSrc, themeIcon, appVersionLabel };
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  min-height: 100svh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px 64px;
}

.login-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 304px);
}

.login-logo {
  display: block;
  object-fit: contain;
  margin-bottom: 28px;
}

.login-card {
  width: 100%;
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  box-shadow: 0 16px 48px rgb(0 0 0 / 12%);
}

.login-card :deep(.p-card-body) { padding: 26px 24px 18px; }
.login-card :deep(.p-card-content) { padding: 0; }
.login-field { margin-bottom: 24px; }
.login-field :deep(.p-inputtext) {
  min-height: 46px;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}
.login-field :deep(.p-inputtext:focus) {
  box-shadow: 0 0 0 3px rgb(39 126 50 / 15%);
}
.login-password { position: relative; }
.login-password :deep(.p-inputtext) { padding-right: 44px; }
.password-toggle {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
}
.password-toggle:hover { background: var(--p-content-hover-background); color: var(--p-text-color); }
.password-toggle:focus-visible { outline: 2px solid var(--p-primary-color); outline-offset: 2px; }
.login-remember { margin-bottom: 22px; }
.login-remember-option { display: flex; align-items: center; gap: 9px; }
.login-remember-option label { font-size: 14px; cursor: pointer; }
.login-hint { margin: 7px 0 0 29px; font-size: 11px; line-height: 1.5; color: var(--p-text-muted-color); }
.submit-button { width: 100%; min-height: 46px; border-radius: 8px; font-size: 15px; }
.login-theme {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 12px;
  border-top: 1px solid var(--p-content-border-color);
}
.theme-toggle-btn { width: 36px; height: 36px; color: var(--p-text-muted-color); }
.theme-toggle-btn :deep(.p-button-icon) { font-size: 1.2rem; }
.login-version { margin: 18px 0 0; font-size: 11px; letter-spacing: 0.04em; color: var(--p-text-muted-color); }
.dark-mode .login-card {
  border-color: rgb(255 255 255 / 8%);
  box-shadow: 0 20px 60px rgb(0 0 0 / 28%);
}
@media (max-height: 600px) {
  .login-page { padding-top: 24px; padding-bottom: 24px; }
  .login-logo { height: 80px; margin-bottom: 18px; }
}
@media (prefers-reduced-motion: reduce) {
  .login-field :deep(.p-inputtext) { transition: none; }
}
</style>
